/**
 * wsr-api schedule-config client (WSRPDM-0014, contract §2).
 *
 * Raw DTOs, no `unwrap()` — same convention as `./tenants.ts` (contract
 * §0.5: wsr-api has no `{success,data}` envelope). `putConfig()` returns the
 * PUT's own re-read response body directly (contract §2.2: "returns the
 * re-read view") — no separate re-GET needed, unlike `updateConfig()` in
 * `./tenants.ts` which re-GETs because that PUT's response is the narrower
 * `IngestionConfigView`.
 *
 * CRITICAL CALLER CONTRACT (contract §2.2): a `null` knob in the PUT body
 * CLEARS it to its default. To change one knob without resetting the other
 * 27, the caller must send the FULL effective knob set (all knobs from the
 * last GET/PUT re-read) with the one edit applied — this module does not
 * enforce that; `src/domain/scheduleKnobs.ts` + `src/routes/engine/` own it.
 * The 4 enable flags are the exception: a `null` flag is left unchanged and
 * may be sent alone (e.g. a single flag toggle).
 */
import { request } from '../client';
import type { PlatformScheduleConfigUpdate, PlatformScheduleConfigView } from './types';

const BASE = '/admin/platform';

/** GET /admin/platform/schedule-config (contract §2.1). */
export async function getConfig(): Promise<PlatformScheduleConfigView> {
  return request<PlatformScheduleConfigView>('GET', `${BASE}/schedule-config`);
}

/**
 * PUT /admin/platform/schedule-config (contract §2.2). Returns the re-read
 * effective view — use it as the new baseline. 400 `INVALID_PLATFORM_CONFIG`
 * carries a structured `field` naming the offending knob, EXCEPT the
 * multi-field engine invariant failure (heartbeat < lease < maxExecution),
 * which has no `field` (see `./errors.ts#wsrApiError`).
 */
export async function putConfig(
  body: PlatformScheduleConfigUpdate,
): Promise<PlatformScheduleConfigView> {
  return request<PlatformScheduleConfigView>('PUT', `${BASE}/schedule-config`, { json: body });
}
